import React, { Component } from 'react';

// Components and Others
import { isFinite } from 'lodash';
import PropTypes from 'prop-types';
import Employment from 'components/modules/deals/read/buyer/Employment';
import PaperAddInformation from 'components/widgets/form/PaperAddInformation';
import CustomerDialog from 'components/modules/customer/read/CustomerDialog';
import EditCustomerDialog from 'components/widgets/customer/EditCustomerDialog';
import BasicCustomerInformation from 'components/modules/customer/read/BasicCustomerInformation';
import InternalBuyerContainer from 'components/containers/deals/read/buyer/InternalBuyerContainer';
import If from 'components/widgets/conditional/If';

import { CustomerType } from 'utils/enum/DealEnum';

// Material UI
import { Grid, Button } from '@material-ui/core';
import CompareArrows from '@material-ui/icons/CompareArrows';
import { withStyles } from '@material-ui/core/styles';
import Address from 'components/modules/deals/read/buyer/Address';

// Utils
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import DealUtils from 'utils/DealUtils';
import DateUtils from 'lib/DateUtils';
import ModalUtils from 'utils/ModalUtils';
import CreateCustomer from 'components/modules/deals/create/dealTab/CreateCustomer';
import { MEDIA_QUERY } from 'utils/enum/Core';

// GraphQL
import DealService from 'services/modules/DealService';

const styles = (theme) => ({
    root: {
        margin: 0,
        width: '100%',
        overflow: 'auto',
        background: theme.palette.background.default,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexDirection: 'column',
            flexWrap: 'nowrap',
            '& .MuiGrid-item': {
                maxWidth: '100%',
            },
        },
    },
    fitContent: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            maxHeight: 'fit-content',
            paddingBottom: '12px',
        },
    },
    centerPanel: {
        textAlign: 'center',
    },
});

class InternalBuyerTab extends Component {
    constructor(props) {
        super(props);
        const keyStore = new KeyStore();
        this.SALES_DEAL_CUSTOMER_WRITE = keyStore.hasPermission(Permission.SALES_DEAL_CUSTOMER_WRITE);
        this.dealService = new DealService();
        this.state = {
            openBuyer: false,
            openCoBuyer: false,
            saving: false,
        };
    }

    componentDidMount() {
        const { getServicesData, subscribeEditBuyer } = this.props;
        subscribeEditBuyer();
        getServicesData();
    }

    componentWillUnmount() {
        const { unsubscribeEditBuyer } = this.props;

        unsubscribeEditBuyer();
    }

    toggleBuyerDialog = (open) => {
        this.setState({ openBuyer: open });
    };

    changeBuyer = (record) => {
        const { accountNumber } = this.props;
        const { customerId } = record;
        const input = {
            dealId: accountNumber,
            customerId,
        };

        this.setState({ saving: true });

        this.dealService.assignBuyer(input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data && data.assignBuyer) {
                    ModalUtils.successMessage([{ message: 'Buyer updated successfully' }]);
                    this.toggleBuyerDialog(false);
                }
            })
            .finally(() => {
                this.setState({ saving: false });
            });
    };

    toggleCoBuyerDialog = (open) => {
        this.setState({ openCoBuyer: open });
    };

    changeCoBuyer = (record) => {
        const { accountNumber } = this.props;
        const { customerId } = record;
        const input = {
            dealId: accountNumber,
            customerId,
        };

        this.setState({ saving: true });

        this.dealService.assignCoBuyer(input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data && data.assignCoBuyer) {
                    ModalUtils.successMessage([{ message: 'Co Buyer updated successfully' }]);
                    this.toggleCoBuyerDialog(false);
                }
            })
            .finally(() => {
                this.setState({ saving: false });
            });
    };

    onCustomerReplaced = (newCustomer) => {
        const { buyer, coBuyer, recordToEdit } = this.props;

        if (recordToEdit?.customerId === buyer?.customerId) {
            this.changeBuyer(newCustomer);
        } else if (recordToEdit?.customerId === coBuyer?.customerId) {
            this.changeCoBuyer(newCustomer);
        }
    };

    renderCoBuyerInformation() {
        const {
            props: {
                editingMode, onChangeEditingMode, accountNumber, coBuyer, coBuyerAddress, coBuyerEmployments,
                clientId, deal, removeCoBuyer, loadingRequest, classes,
            },
        } = this;
        const { openCoBuyer, saving } = this.state;
        const isNotPosted = !DateUtils.isValid(deal.postedDate);
        const hasWritePermission = DealUtils.clientIdIsWeb(clientId) && isNotPosted && this.SALES_DEAL_CUSTOMER_WRITE;

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <BasicCustomerInformation
                        title="Co Buyer"
                        record={coBuyer}
                        open={openCoBuyer}
                        loading={loadingRequest}
                        showDeleteOption
                        onClickRemove={removeCoBuyer}
                        showEditCustomer={hasWritePermission}
                        onClickPrimaryDialog={this.changeCoBuyer}
                        disablePrimaryButtonDialog={saving}
                        hasWritePermission={hasWritePermission}
                        onClickPrimary={() => this.toggleCoBuyerDialog(true)}
                        onClickSecondaryDialog={() => this.toggleCoBuyerDialog(false)}
                        onUseSuggested={this.changeCoBuyer}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Address
                        deal={deal}
                        clientId={clientId}
                        editingMode={editingMode}
                        type={CustomerType.CO_BUYER}
                        buyerAddress={coBuyerAddress}
                        accountNumber={accountNumber}
                        className={classes.fitContent}
                        customerId={coBuyer.customerId}
                        onChangeEditingMode={onChangeEditingMode}
                        customerCurrentAddressId={coBuyer.currentAddressId}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Employment
                        clientId={clientId}
                        editingMode={editingMode}
                        postedDate={deal.postedDate}
                        type={CustomerType.CO_BUYER}
                        accountNumber={accountNumber}
                        className={classes.fitContent}
                        customerId={coBuyer.customerId}
                        employments={coBuyerEmployments}
                        onChangeEditingMode={onChangeEditingMode}
                        dealCurrentEmploymentId={coBuyer.currentEmploymentId}
                    />
                </Grid>
            </Grid>
        );
    }

    renderCoBuyerSection() {
        const {
            loading, coBuyer, onOpenCustomer, clientId,
        } = this.props;

        if (loading) return null;

        if (isFinite(coBuyer.customerCode)) return this.renderCoBuyerInformation();

        return DealUtils.clientIdIsWeb(clientId) ? <PaperAddInformation label="Add Co Buyer" onClickLeftLabel={onOpenCustomer} /> : null;
    }

    render() {
        const {
            props: {
                classes, buyerAddress, buyer, coBuyer, onCloseCustomer, clientId, loadingRequest,
                accountNumber, buyerEmployments, editingMode, onChangeEditingMode, recordToEdit, editingStatus,
                openCustomer, onDoubleClick, deal, loading, toggleEditCustomer, openEditCustomer, swapBuyerCoBuyer,
                openCreateCustomer, toggleCreateCustomer,
            },
        } = this;
        const allowEdit = DealUtils.allowEditDeal(deal?.stage);
        const { isEditing } = editingStatus;
        const isNotPosted = !DateUtils.isValid(deal.postedDate);
        const hasWritePermission = DealUtils.clientIdIsWeb(clientId) && isNotPosted && this.SALES_DEAL_CUSTOMER_WRITE;
        const { openBuyer, saving } = this.state;

        return (
            <Grid container spacing={3} className={classes.root}>
                <Grid item xs={5}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <BasicCustomerInformation
                                title="Buyer"
                                record={buyer}
                                open={openBuyer}
                                loading={loadingRequest}
                                showEditCustomer={hasWritePermission}
                                onClickPrimaryDialog={this.changeBuyer}
                                disablePrimaryButtonDialog={saving}
                                hasWritePermission={hasWritePermission}
                                onClickPrimary={() => this.toggleBuyerDialog(true)}
                                onClickSecondaryDialog={() => this.toggleBuyerDialog(false)}
                                onUseSuggested={this.changeBuyer}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Address
                                loading={loading}
                                clientId={clientId}
                                deal={deal}
                                editingMode={editingMode}
                                buyerAddress={buyerAddress}
                                editingStatus={editingStatus}
                                customerCurrentAddressId={buyer.currentAddressId}
                                accountNumber={accountNumber}
                                customerId={buyer.customerId}
                                onChangeEditingMode={onChangeEditingMode}
                                className={classes.fitContent}
                                type={CustomerType.BUYER}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Employment
                                loading={loading}
                                clientId={clientId}
                                editingMode={editingMode}
                                type={CustomerType.BUYER}
                                postedDate={deal.postedDate}
                                accountNumber={accountNumber}
                                customerId={buyer.customerId}
                                editingStatus={editingStatus}
                                employments={buyerEmployments}
                                className={classes.fitContent}
                                onChangeEditingMode={onChangeEditingMode}
                                dealCurrentEmploymentId={buyer.currentEmploymentId}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={2} className={classes.centerPanel}>
                    <If condition={
                        isFinite(coBuyer.customerCode)
                            && DealUtils.clientIdIsWeb(clientId)
                            && isNotPosted
                            && allowEdit
                            && this.SALES_DEAL_CUSTOMER_WRITE
                    }
                    >
                        <Button
                            startIcon={<CompareArrows />}
                            onClick={swapBuyerCoBuyer}
                            disabled={loadingRequest || isEditing}
                        >
                            Swap
                        </Button>
                    </If>
                </Grid>
                <Grid item xs={5}>
                    {this.renderCoBuyerSection()}
                </Grid>
                {openCustomer && !isEditing && (
                    <CustomerDialog
                        open={openCustomer}
                        toggleModal={onCloseCustomer}
                        onSelectRow={onDoubleClick}
                        customerTag={CustomerType.CO_BUYER}
                        toggleCreateCustomerPanel={() => { onCloseCustomer(); toggleCreateCustomer(CustomerType.CO_BUYER); }}
                    />
                )}
                {openEditCustomer && !isEditing && (
                    <EditCustomerDialog
                        open={openEditCustomer}
                        record={recordToEdit}
                        toggleModal={toggleEditCustomer}
                        onUseSuggested={this.onCustomerReplaced}
                    />
                )}
                {openCreateCustomer && <CreateCustomer toggleCreateCustomer={toggleCreateCustomer} onSetCustomer={onDoubleClick} />}
            </Grid>
        );
    }
}

InternalBuyerTab.propTypes = {
    buyer: PropTypes.shape({
        customerId: PropTypes.string,
        dob: PropTypes.string,
        ssn: PropTypes.string,
        dln: PropTypes.string,
        email: PropTypes.string,
        title: PropTypes.string,
        lastName: PropTypes.string,
        cellPhone: PropTypes.string,
        homePhone: PropTypes.string,
        workPhone: PropTypes.string,
        firstName: PropTypes.string,
        currentAddressId: PropTypes.string,
        currentEmploymentId: PropTypes.string,
        isBusiness: PropTypes.bool,
    }).isRequired,
    buyerAddress: PropTypes.arrayOf(
        PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            county: PropTypes.string,
            country: PropTypes.string,
            phone: PropTypes.string,
            housingStatus: PropTypes.string,
            mortgageOrRent: PropTypes.number,
            totalMonths: PropTypes.number,
        }),
    ).isRequired,
    buyerEmployments: PropTypes.arrayOf(
        PropTypes.shape({
            dealPreviousEmploymentId: PropTypes.string,
            customerEmploymentId: PropTypes.string,
            status: PropTypes.string,
            employer: PropTypes.string,
            title: PropTypes.string,
            totalMonths: PropTypes.number,
            monthlyIncome: PropTypes.number,
            workPhone: PropTypes.string,
            otherIncomeMonthly: PropTypes.number,
            otherIncomeSource: PropTypes.string,
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            county: PropTypes.string,
            country: PropTypes.string,
            isCurrentEmployment: PropTypes.bool.isRequired,
        }),
    ).isRequired,
    coBuyer: PropTypes.shape({
        customerCode: PropTypes.number,
        customerId: PropTypes.string,
        dob: PropTypes.string,
        ssn: PropTypes.string,
        dln: PropTypes.string,
        email: PropTypes.string,
        title: PropTypes.string,
        lastName: PropTypes.string,
        cellPhone: PropTypes.string,
        homePhone: PropTypes.string,
        workPhone: PropTypes.string,
        firstName: PropTypes.string,
        currentAddressId: PropTypes.string,
        currentEmploymentId: PropTypes.string,
        isBusiness: PropTypes.bool,
    }).isRequired,
    coBuyerAddress: PropTypes.arrayOf(
        PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            county: PropTypes.string,
            country: PropTypes.string,
            phone: PropTypes.string,
            housingStatus: PropTypes.string,
            mortgageOrRent: PropTypes.number,
            totalMonths: PropTypes.number,
        }),
    ).isRequired,
    coBuyerEmployments: PropTypes.arrayOf(
        PropTypes.shape({
            dealPreviousEmploymentId: PropTypes.string,
            customerEmploymentId: PropTypes.string,
            status: PropTypes.string,
            employer: PropTypes.string,
            title: PropTypes.string,
            totalMonths: PropTypes.number,
            monthlyIncome: PropTypes.number,
            workPhone: PropTypes.string,
            otherIncomeMonthly: PropTypes.number,
            otherIncomeSource: PropTypes.string,
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            county: PropTypes.string,
            country: PropTypes.string,
            isCurrentEmployment: PropTypes.bool.isRequired,
        }),
    ).isRequired,
    accountNumber: PropTypes.number.isRequired,
    getServicesData: PropTypes.func.isRequired,
    subscribeEditBuyer: PropTypes.func.isRequired,
    unsubscribeEditBuyer: PropTypes.func.isRequired,
    classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    editingMode: PropTypes.shape({
        isEditing: PropTypes.bool,
        sectionName: PropTypes.string,
    }).isRequired,
    onChangeEditingMode: PropTypes.func.isRequired,
    onOpenCustomer: PropTypes.func.isRequired,
    onCloseCustomer: PropTypes.func.isRequired,
    openCustomer: PropTypes.bool.isRequired,
    onDoubleClick: PropTypes.func.isRequired,
    toggleEditCustomer: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    deal: PropTypes.object,
    clientId: PropTypes.string.isRequired,
    recordToEdit: PropTypes.object,
    openEditCustomer: PropTypes.bool.isRequired,
    removeCoBuyer: PropTypes.func.isRequired,
    swapBuyerCoBuyer: PropTypes.func.isRequired,
    loadingRequest: PropTypes.bool.isRequired,
    editingStatus: PropTypes.object,
    openCreateCustomer: PropTypes.bool.isRequired,
    toggleCreateCustomer: PropTypes.func.isRequired,
};

InternalBuyerTab.defaultProps = {
    deal: {},
    recordToEdit: null,
    editingStatus: {
        isEditing: false,
        editingById: null,
        editingBy: null,
    },
};

export default withStyles(styles)(InternalBuyerContainer(InternalBuyerTab));
